import { useRef, useState } from "react";
import { Camera, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "@/lib/api";
import { ACCEPTED_TYPES, detectType, MAX_FILE_SIZE, uploadFile } from "@/lib/upload";
import { cn } from "@/lib/utils";

export function PhotoUploader({ meta, onUploaded, allowPdf = false, className, testId = "photo-uploader" }) {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [jobs, setJobs] = useState([]);

  const handle = async (list) => {
    for (const f of Array.from(list || [])) {
      const type = detectType(f);
      if (!ACCEPTED_TYPES.includes(type) || (type === "application/pdf" && !allowPdf)) {
        toast.error(`Format non accepté : ${f.name}`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} dépasse 15 Mo`);
        continue;
      }
      const id = `${Date.now()}-${Math.random()}`;
      setJobs((j) => [...j, { id, name: f.name, progress: 0 }]);
      try {
        const doc = await uploadFile(f, meta, (p) => setJobs((j) => j.map((x) => (x.id === id ? { ...x, progress: p } : x))));
        onUploaded?.(doc);
      } catch (e) {
        toast.error(errorMessage(e));
      } finally {
        setJobs((j) => j.filter((x) => x.id !== id));
      }
    }
  };
  const onPick = (e) => {
    handle(e.target.files);
    e.target.value = "";
  };

  return (
    <div className={cn("flex flex-col gap-1.5", className)} data-testid={testId}>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid={`${testId}-camera-input`} />
      <input ref={fileRef} type="file" accept={allowPdf ? "image/*,.heic,.heif,application/pdf" : "image/*,.heic,.heif"} multiple className="hidden" onChange={onPick} data-testid={`${testId}-file-input`} />
      <button type="button" onClick={() => camRef.current?.click()} className="flex min-h-[64px] flex-1 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 text-primary transition-colors hover:bg-primary/10" data-testid={`${testId}-camera-button`}>
        <Camera className="h-6 w-6" />
        <span className="text-[11px] font-semibold">Photo</span>
      </button>
      <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center justify-center gap-1 rounded-lg border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted" data-testid={`${testId}-file-button`}>
        <Upload className="h-3 w-3" /> {allowPdf ? "Fichier / PDF" : "Galerie"}
      </button>
      {jobs.map((j) => (
        <div key={j.id} className="text-[10px] text-muted-foreground" data-testid={`${testId}-progress`}>
          <div className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin text-primary" /> {j.progress}%</div>
          <div className="mt-0.5 h-1 rounded bg-muted"><div className="h-1 rounded bg-primary transition-all" style={{ width: `${j.progress}%` }} /></div>
        </div>
      ))}
    </div>
  );
}
